import React, { Fragment, useContext, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useNavigate } from "react-router-dom";
import {
  getContentGroupsViewPath,
  getModuleViewPath,
  getReviewViewPath,
  getUngroupedItemsViewPath,
  OrganizeViews,
} from "../organizeUtil";
import { useCurrentOrgId, useIsIapReviewer } from "../../../auth/authUtils";
import SnackAlert from "../../../components/SnackAlert";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { OrganizeContext } from "../organize-context";
import { useSelected } from "../OrganizeProvider";
import {
  useContentByStatus,
  useContentInProcessing,
} from "../../creatorServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleRight } from "@fortawesome/pro-regular-svg-icons";

import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";

import CollapseIcon from "./CollapseIcon";
import SidebarModuleTree from "./SidebarModuleTree";
import SidebarTreeItem from "./SidebarTreeItem";
import SidebarUngroupedContentGroupsTree from "./SidebarUngroupedContentGroupsTree";

// Default tree expand/collapse icons (x-tree-view slots take component types)
const TreeExpandIcon = () => <FontAwesomeIcon icon={faAngleRight} />;
const TreeCollapseIcon = () => <FontAwesomeIcon icon={faAngleDown} />;

const useStyles = makeStyles(theme => ({
  header: {
    fontWeight: theme.typography.fontWeightMedium,
  },

  icon: {
    padding: 2,
  },

  tree: {
    width: "100%",
  },
}));

const OrganizeSidebar = props => {
  const classes = useStyles();
  const navigate = useNavigate();
  const currentOrgId = useCurrentOrgId();
  const isIapReviewer = useIsIapReviewer();
  const { updateDragSourceParent } = useContext(OrganizeContext);

  const { removeAllSelected } = useSelected();

  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  const { selected, expanded, handleExpanded } = props;

  const handleLabelClick = viewPath => event => {
    event.preventDefault();
    updateDragSourceParent({});
    removeAllSelected();
    navigate(viewPath);
  };

  const handleModuleLabelClick = id => event => {
    removeAllSelected();

    const modulePath = getModuleViewPath(id);
    navigate(modulePath);
  };

  // Content Totals
  const approvedResults = useContentByStatus({ status: "approved" });
  const [approvedCount, setApprovedCount] = useState("-");
  useEffect(() => {
    if (approvedResults.isSuccess) {
      setApprovedCount(approvedResults.data.count);
    }
  }, [approvedResults.isSuccess, approvedResults.data]);

  const pendingResults = useContentByStatus({ status: "pending" });
  const [pendingCount, setPendingCount] = useState("-");
  useEffect(() => {
    if (pendingResults.isSuccess) {
      setPendingCount(pendingResults.data.count);
    }
  }, [pendingResults.isSuccess, pendingResults.data]);

  const rejectedResults = useContentByStatus({ status: "rejected" });
  const [rejectedCount, setRejectedCount] = useState("-");
  useEffect(() => {
    if (rejectedResults.isSuccess) {
      setRejectedCount(rejectedResults.data.count);
    }
  }, [rejectedResults.isSuccess, rejectedResults.data]);

  const processingResults = useContentInProcessing(currentOrgId, {
    limit: 0,
    offset: 0,
  });
  const [processingCount, setProcessingCount] = useState("-");
  useEffect(() => {
    if (processingResults.isSuccess) {
      setProcessingCount(processingResults.data.totalElements);
    }
  }, [processingResults.isSuccess, processingResults.data]);

  const [modulesLoading, setModulesLoading] = useState(false);
  const [ungroupedLoading, setUngroupedLoading] = useState(false);

  return (
    <Fragment>
      <SnackAlert handler={snackAlertHandler} />

      <SimpleTreeView
        aria-label="organize content sidebar"
        classes={{ root: classes.tree }}
        slots={{ collapseIcon: TreeCollapseIcon, expandIcon: TreeExpandIcon }}
        onExpandedItemsChange={handleExpanded}
        selectedItems={selected}
        expandedItems={expanded}
      >
        <SidebarTreeItem
          label="Review"
          nodeId={OrganizeViews.REVIEW}
          nodeType="root"
          nestedTree={
            <Fragment>
              {!isIapReviewer && (
                <SidebarTreeItem
                  label={`Processing (${processingCount})`}
                  nodeType="root"
                  // [TODO]: add processing variant
                  variant="pending"
                  nodeId={OrganizeViews.PROCESSING}
                  onLabelClick={handleLabelClick(
                    getReviewViewPath(OrganizeViews.PROCESSING),
                  )}
                />
              )}

              <SidebarTreeItem
                label={`Pending (${pendingCount})`}
                nodeType="root"
                variant="pending"
                nodeId={OrganizeViews.PENDING}
                onLabelClick={handleLabelClick(
                  getReviewViewPath(OrganizeViews.PENDING),
                )}
              />

              <SidebarTreeItem
                label={`Approved (${approvedCount})`}
                nodeType="root"
                variant="approved"
                nodeId={OrganizeViews.APPROVED}
                onLabelClick={handleLabelClick(
                  getReviewViewPath(OrganizeViews.APPROVED),
                )}
              />

              <SidebarTreeItem
                label={`Rejected (${rejectedCount})`}
                nodeType="root"
                variant="rejected"
                nodeId={OrganizeViews.REJECTED}
                onLabelClick={handleLabelClick(
                  getReviewViewPath(OrganizeViews.REJECTED),
                )}
              />
            </Fragment>
          }
        />

        {!isIapReviewer && (
          <SidebarTreeItem
            label="Build"
            nodeId={OrganizeViews.ORGANIZE}
            nodeType="root"
            nestedTree={
              <Fragment>
                <SidebarTreeItem
                  label="Unorganized"
                  nodeId={OrganizeViews.UNGROUPED_ITEMS}
                  nodeType="root"
                  collapseIcon={<CollapseIcon loading={ungroupedLoading} />}
                  onLabelClick={handleLabelClick(getUngroupedItemsViewPath())}
                  nestedTree={
                    <SidebarUngroupedContentGroupsTree
                      showAlert={showAlert}
                      setLoading={setUngroupedLoading}
                    />
                  }
                />

                <SidebarTreeItem
                  label="Organized"
                  nodeId={OrganizeViews.CONTENT_GROUPS}
                  nodeType="root"
                  collapseIcon={<CollapseIcon loading={modulesLoading} />}
                  onLabelClick={handleLabelClick(getContentGroupsViewPath())}
                  nestedTree={
                    <SidebarModuleTree
                      onLabelClick={handleModuleLabelClick}
                      showAlert={showAlert}
                      setLoading={setModulesLoading}
                    />
                  }
                />
              </Fragment>
            }
          />
        )}
      </SimpleTreeView>
    </Fragment>
  );
};

export default OrganizeSidebar;
